import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useOrgId } from './useOrgId';
import { 
  fetchOrgEntitlements, 
  processEntitlements,
  type EntitlementRecord 
} from '@/services/entitlements';
import type { EntitlementKey } from '@/types/entitlements';

interface UseEntitlementsReturn {
  entitlements: Record<EntitlementKey, boolean | number | string>;
  hasEntitlement: (key: EntitlementKey) => boolean;
  getEntitlementValue: (key: EntitlementKey) => boolean | number | string;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to fetch and check organization entitlements
 * Entitlements are admin-granted overrides that bypass subscription checks
 */
export function useEntitlements(): UseEntitlementsReturn {
  const { user } = useAuth();
  const { data: orgId, isLoading: orgIdLoading } = useOrgId();
  
  const { data: rawEntitlements, isLoading: entitlementsLoading, refetch } = useQuery({
    queryKey: ['entitlements', orgId],
    queryFn: () => fetchOrgEntitlements(orgId!),
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: true,
  });
  
  const entitlements = processEntitlements(rawEntitlements || []);
  
  const hasEntitlement = (key: EntitlementKey): boolean => {
    const value = entitlements[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;
    return !!value;
  };
  
  const getEntitlementValue = (key: EntitlementKey): boolean | number | string => {
    return entitlements[key];
  };
  
  return {
    entitlements,
    hasEntitlement,
    getEntitlementValue,
    isLoading: (entitlementsLoading || orgIdLoading) && !!user,
    refetch,
  };
}
